import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Landmark, LucideIcon, RadioTower, SearchIcon, ShoppingBag, Workflow, Zap } from "lucide-react";
import { useJourneyCatalog } from "./customer-journey/use-catalog";
import { JourneyTemplate } from "./customer-journey/types";

// "All workflows > Prebuilt templates" tab — replaces the old placeholder
// "Prebuilt Templates" sidebar page. Same source and same hand-off as the
// New Bot modal (GET /v1/bot/journeys → the builder with `journeyId`), just
// laid out as a browsable gallery. No design yet; styled to match All Bots.

const CATEGORY_ICON: Record<string, { icon: LucideIcon; cls: string }> = {
  Banking: { icon: Landmark, cls: "bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400" },
  Commerce: { icon: ShoppingBag, cls: "bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400" },
  Telco: { icon: RadioTower, cls: "bg-sky-50 text-sky-600 dark:bg-sky-500/15 dark:text-sky-400" },
  Telecom: { icon: RadioTower, cls: "bg-sky-50 text-sky-600 dark:bg-sky-500/15 dark:text-sky-400" },
};
const DEFAULT_ICON = { icon: Workflow, cls: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300" };

const CHANNEL_LABEL: Record<string, string> = { whatsapp: "WhatsApp", sms: "SMS", rcs: "RCS", email: "Email", push: "Push" };

// "salary_credit_detected" → "Salary credit detected"
const humanize = (id: string) => id.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

const PrebuiltTemplates: React.FC = () => {
  const navigate = useNavigate();
  const { loading, journeyTemplates } = useJourneyCatalog();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");

  const categories = useMemo(() => Array.from(new Set(journeyTemplates.map((t) => t.category).filter(Boolean))), [journeyTemplates]);
  const q = search.trim().toLowerCase();
  const visible = journeyTemplates.filter(
    (t) => (category === "all" || t.category === category) && (!q || t.name.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q))
  );

  const openTemplate = (t: JourneyTemplate) =>
    navigate("/automate/bot-studio/customer-journey/new", { state: { journeyId: t.id, journeyName: t.name } });

  const chip = (key: string, label: string, count: number) => (
    <button
      key={key}
      type="button"
      onClick={() => setCategory(key)}
      className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
        category === key
          ? "bg-indigo-600 text-white border-indigo-600"
          : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-800"
      }`}
    >
      {label} <span className={category === key ? "text-indigo-100" : "text-gray-400"}>{count}</span>
    </button>
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex flex-wrap gap-2">
          {chip("all", "All", journeyTemplates.length)}
          {categories.map((c) => chip(c, c, journeyTemplates.filter((t) => t.category === c).length))}
        </div>
        <div className="flex items-center gap-2 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 w-full sm:w-72 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
          <SearchIcon size={16} />
          <input
            className="outline-none text-sm text-gray-600 dark:text-gray-300 bg-transparent w-full placeholder-gray-400 dark:placeholder-gray-500"
            placeholder="Search templates"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading && journeyTemplates.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-52 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 animate-pulse" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div className="border border-dashed border-gray-200 dark:border-gray-800 rounded-xl py-16 text-center text-sm font-semibold text-[#7B7B7B] dark:text-gray-500">
          No templates match your search.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {visible.map((t) => {
            const { icon: Icon, cls } = CATEGORY_ICON[t.category] || DEFAULT_ICON;
            return (
              <div
                key={t.id}
                className="group flex flex-col rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 hover:border-indigo-300 dark:hover:border-indigo-500/50 hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${cls}`}>
                    <Icon size={20} />
                  </div>
                  <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-full px-2.5 py-0.5">
                    {t.category || "General"}
                  </span>
                </div>

                <h3 className="mt-4 text-base font-semibold text-gray-800 dark:text-gray-100">{t.name}</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{t.description}</p>

                {t.triggerEvent && (
                  <p className="mt-3 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                    <Zap size={13} className="text-amber-500 shrink-0" />
                    Starts when: <span className="font-medium text-gray-700 dark:text-gray-200">{humanize(t.triggerEvent)}</span>
                  </p>
                )}

                <div className="mt-auto pt-4 flex items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-1.5">
                    {t.channels.map((c) => (
                      <span key={c} className="text-[11px] font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-md px-2 py-0.5">
                        {CHANNEL_LABEL[c.toLowerCase()] || c}
                      </span>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => openTemplate(t)}
                    className="flex items-center gap-1.5 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 shrink-0"
                  >
                    Use template
                    <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PrebuiltTemplates;
